import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {createStyles, Divider, Grid} from '@mantine/core';
import React, {useEffect, useMemo, useState} from 'react';

import {AntibodyComponentType} from 'antibodies/AntibodyConstants';
import {useAntibodyFormatsQuery} from 'antibodies/hooks/useAntibodyFormatsQuery';
import {useImplementedAntibodySchemaInterfacesQuery} from 'antibodies/hooks/useImplementedAntibodySchemaInterfacesQuery';
import AntibodyComponentSvg from 'antibodies/nested-data-creation/antibody-component-visualization/AntibodyComponentSvg';
import {buildAntibodySchemaFormatMaps} from 'antibodies/nested-data-creation/AntibodyNestedDataCreationUtil';
import {
  ALLOWED_ANTIBODY_FORMAT_IDS,
  NDCSchemaConfig,
  NestedDataType,
} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import {GetNDCSchemaConfig} from 'antibodies/nested-data-creation/panes/__generated/GetNDCSchemaConfig';
import AntibodyFormatPicker, {
  type AntibodyTileProps,
} from 'antibodies/schema-configuration/AntibodyFormatPicker';
import {assert} from 'Asserts';
import {OBJECTS_REGISTERABLE_PERMISSION} from 'auth/PermissionConstants';
import {pluralize, prependAOrAn} from 'BenchlingUtil';
import {type TagSchema} from 'constants/RegistryConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {AntibodyFormatGraphQL} from '../../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';

const useStyles = createStyles((theme) => ({
  formatPicker: {
    h4: {
      marginBottom: theme.spacing.sm,
    },
    p: {
      marginBottom: theme.spacing.md,
    },
  },
  buttonWrapper: {
    width: 'max-content',
  },
  button: {
    border: 'none',
    padding: 0,
    backgroundColor: 'transparent',
    cursor: 'default',

    // Center the glyph over the caption
    display: 'block',
    margin: '0 auto',
    width: 'max-content',
  },
  buttonLabel: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    display: 'flex',
    justifyContent: 'center',
    marginTop: '4px',
  },
  inputOutputGrid: {
    h5: {
      color: theme.colors.gray[7],
    },
  },
  inputOutputSection: {
    display: 'flex',
    gap: '16px',
  },
  banner: {
    marginTop: '16px',
  },
}));

interface InputOutputComponentButtonProps {
  componentType: AntibodyComponentType;
  componentName: string;
  isSelected: boolean;
}

function InputOutputComponentButton({
  componentType,
  componentName,
  isSelected,
}: InputOutputComponentButtonProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={classes.buttonWrapper}>
      <button
        className={classes.button}
        onClick={noop}
        type="button"
        aria-label={`${componentName} option`}
        aria-pressed={isSelected}
      >
        <AntibodyComponentSvg
          componentType={componentType}
          size={90}
          useDefaultBackground={false}
          useDefaultBorder={false}
        />
      </button>
      <div className={classes.buttonLabel}>{componentName}</div>
    </div>
  );
}

export const NDC_CONFIG_QUERY = gql`
  query GetNDCSchemaConfig($registryId: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    ndcSchemaConfig(registryId: $registryId) {
      ... on NDCSchemaConfigNode {
        aaDomainSchemaId
        dnaPairSchemaId
      }
    }
  }
`;

export function getMissingSchemaErrorMessage(missingSchemas: Array<string>): string {
  const missingSchemasString = missingSchemas.join(' and ');
  return `Create ${pluralize(missingSchemas.length, prependAOrAn(missingSchemasString), `the ${missingSchemasString}`)} ${pluralize(missingSchemas.length, 'schema', 'schemas')} in order to create proteins.`;
}

/**
 * Nested data creation modal step that allows the user to select the format of the antibodies they want to create.
 *
 * Current limitations:
 * - Only supports selecting from antibody formats that involve a single variable pair (VH/VL).
 * - The input and output components are not configurable by the user: the input is always DNA domain
 *   and both AA domains and AA chains must always be created as outputs.
 */
export interface SelectFormatPaneProps {
  selectedRegistryId: string | null;
  setSelectedRegistryIdAndDependentConfig: (
    registryId: string | null,
    ndcSchemaConfig: NDCSchemaConfig
  ) => void;
  selectedFormatId: string | null;
  setSelectedFormat: (format: AntibodyFormatGraphQL | null) => void;
  selectedSchemaId: string | null;
  setSelectedSchemaId: (schemaId: string | null) => void;
  startingDataType: NestedDataType;
  outputTypes: ReadonlySet<NestedDataType>;
}

export default function SelectFormatPane({
  selectedRegistryId,
  setSelectedRegistryIdAndDependentConfig,
  selectedFormatId,
  setSelectedFormat,
  selectedSchemaId,
  setSelectedSchemaId,
  startingDataType,
  outputTypes,
}: SelectFormatPaneProps): JSX.Element {
  const {classes} = useStyles();
  const {status: antibodyFormatsQueryStatus, antibodyFormats} = useAntibodyFormatsQuery();

  const {status: implementedAntibodySchemaInterfacesQueryStatus, implementedAntibodySchemaInterfaces} =
    useImplementedAntibodySchemaInterfacesQuery();
  const {schemaIdToFormat, formatIdToSchemaIds, allSchemaIds} = useMemo(
    () =>
      buildAntibodySchemaFormatMaps(
        implementedAntibodySchemaInterfaces.filter(
          (implementedInterface) => !implementedInterface.schema.archived
        )
      ),
    [implementedAntibodySchemaInterfaces]
  );

  // After a schema is selected, we derive the registry ID from it and need to wait for the dependent config
  // to be fetched before we can set the selected registry and dependent config.
  const [schemaRegistryId, setSchemaRegistryId] = useState<string | null>(selectedRegistryId);
  const [registryErrorMessage, setRegistryErrorMessage] = useState<string | null>(null);
  const ndcConfigQueryResult = useSkippableBenchlingQuery<GetNDCSchemaConfig>(NDC_CONFIG_QUERY, {
    variables: {registryId: schemaRegistryId!},
    skip: selectedSchemaId == null,
  });

  // Extract the results for easier useEffect dependencies. We can't use the query results as a dependency
  // because useEffect does a shallow comparison, and the identity of the object changes continuously.
  const ndcConfigIsInaccessible =
    ndcConfigQueryResult.type === QueryResultType.SUCCESS &&
    isInaccessible(ndcConfigQueryResult.data.ndcSchemaConfig);
  const dnaPairSchemaId =
    ndcConfigQueryResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(ndcConfigQueryResult.data.ndcSchemaConfig)
      ? ndcConfigQueryResult.data.ndcSchemaConfig.dnaPairSchemaId
      : null;
  const aaDomainSchemaId =
    ndcConfigQueryResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(ndcConfigQueryResult.data.ndcSchemaConfig)
      ? ndcConfigQueryResult.data.ndcSchemaConfig.aaDomainSchemaId
      : null;

  useEffect(() => {
    if (ndcConfigQueryResult.type === QueryResultType.SUCCESS) {
      // If the registry was selected, it should be accessible.
      assert(!ndcConfigIsInaccessible, 'Expected registry to be accessible');
      // If the query is a success and the registry is accessible, missing schema ids mean they don't exist.
      const missingSchemas: Array<string> = [];
      if (dnaPairSchemaId == null) {
        missingSchemas.push('DNA pair');
      }
      if (aaDomainSchemaId == null) {
        missingSchemas.push('AA domain');
      }
      if (missingSchemas.length > 0) {
        setRegistryErrorMessage(getMissingSchemaErrorMessage(missingSchemas));
      } else {
        setRegistryErrorMessage(null);
      }
      const ndcSchemaConfig: NDCSchemaConfig = {
        aaDomainSchemaId,
        dnaPairSchemaId,
      };
      setSelectedRegistryIdAndDependentConfig(schemaRegistryId, ndcSchemaConfig);
    }
  }, [
    ndcConfigQueryResult.type,
    ndcConfigIsInaccessible,
    dnaPairSchemaId,
    aaDomainSchemaId,
    setSelectedRegistryIdAndDependentConfig,
    schemaRegistryId,
  ]);

  const antibodyTileProps: Array<AntibodyTileProps> =
    antibodyFormatsQueryStatus === QueryResultType.SUCCESS
      ? antibodyFormats
          .filter(({id}) => ALLOWED_ANTIBODY_FORMAT_IDS.includes(id))
          // Only show formats that have a defined antibody schema using this format
          .filter(({id}) => formatIdToSchemaIds.has(id))
          .map(
            (format): AntibodyTileProps => ({
              format,
              showLabels: false,
              disabled: false,
            })
          )
      : [];

  function onSelectFormatId(formatId: string): void {
    assert(antibodyFormatsQueryStatus === QueryResultType.SUCCESS, 'Expected antibody formats to be loaded');
    const format = antibodyFormats.find(({id}) => id === formatId);
    assert(format != null, 'Expected format to be found in antibodyFormats');
    setSelectedFormat(format);
    if (selectedSchemaId != null) {
      assert(schemaIdToFormat.has(selectedSchemaId), 'Expected schema to be found in schemaIdToFormat');
      if (formatId !== schemaIdToFormat.get(selectedSchemaId)!.id) {
        setSelectedSchemaId(null);
        setSchemaRegistryId(null);
        setRegistryErrorMessage(null);
      }
    }
  }

  const onSelectSchema = (tagSchema: TagSchema | null): void => {
    setSelectedSchemaId(tagSchema?.id ?? null);
    setSchemaRegistryId(tagSchema?.registryId ?? null);
    if (tagSchema != null) {
      assert(schemaIdToFormat.has(tagSchema.id), 'Expected schema to be found in schemaIdToFormat');
      setSelectedFormat(schemaIdToFormat.get(tagSchema.id)!);
    }
  };

  const filteredSchemaIds =
    selectedFormatId == null
      ? new Set(
          [...allSchemaIds].filter((schemaId) =>
            ALLOWED_ANTIBODY_FORMAT_IDS.includes(schemaIdToFormat.get(schemaId)!.id)
          )
        )
      : formatIdToSchemaIds.get(selectedFormatId)!;

  return (
    <div>
      {antibodyFormatsQueryStatus === QueryResultType.ERROR && (
        <div className={classes.banner}>
          <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading protein formats" />
        </div>
      )}

      {implementedAntibodySchemaInterfacesQueryStatus === QueryResultType.ERROR && (
        <div className={classes.banner}>
          <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading protein schemas" />
        </div>
      )}

      {registryErrorMessage != null && (
        <div className={classes.banner}>
          <InlineBanner bannerType={InlineBannerType.ERROR} text={registryErrorMessage} />
        </div>
      )}

      <div className={classes.formatPicker}>
        <h4>Format</h4>
        <p>Select the format of the proteins you want to create</p>
        <AntibodyFormatPicker
          antibodyTileProps={antibodyTileProps}
          disabled={false}
          selectedFormat={selectedFormatId}
          setSelectedFormat={onSelectFormatId}
          enableCreateNew={false}
          onCreationPaneOpenChange={noop}
        />
      </div>

      <Grid mt="md">
        <Grid.Col span={6} {...getTestProps({component: 'SelectFormatPane', element: 'schemaSelector'})}>
          <FormGroup hasError={implementedAntibodySchemaInterfacesQueryStatus === QueryResultType.ERROR}>
            <FieldLabel label="Schema" isRequired={true} />
            {implementedAntibodySchemaInterfacesQueryStatus === QueryResultType.PENDING ? (
              <div>
                <HorizontalThrobber />
              </div>
            ) : (
              <TagSchemaSelector
                selectedTagSchemaId={selectedSchemaId}
                onChange={onSelectSchema}
                tagSchemaFilterFn={(tagSchema) => filteredSchemaIds.has(tagSchema.id)}
                shouldDefaultIfSingleItem={true}
                requiredPermissions={[OBJECTS_REGISTERABLE_PERMISSION]}
              />
            )}
          </FormGroup>
        </Grid.Col>
      </Grid>

      <Divider my="md" />

      <p>Input DNA variable domains and AA constant domains to create AA chains and proteins</p>

      <Grid className={classes.inputOutputGrid}>
        <Grid.Col span={6} {...getTestProps({component: 'SelectFormatPane', element: 'inputsSection'})}>
          <h5>INPUT COMPONENTS</h5>
          <div className={classes.inputOutputSection}>
            <InputOutputComponentButton
              componentType={AntibodyComponentType.DNA_DOMAIN}
              componentName="DNA variable domain"
              isSelected={startingDataType === NestedDataType.DNA_DOMAIN}
            />
            <InputOutputComponentButton
              componentType={AntibodyComponentType.AA_DOMAIN}
              componentName="AA constant domain"
              isSelected={startingDataType === NestedDataType.AA_DOMAIN}
            />
          </div>
        </Grid.Col>

        <Grid.Col span={6} {...getTestProps({component: 'SelectFormatPane', element: 'outputsSection'})}>
          <h5>OUTPUT COMPONENTS</h5>
          <div className={classes.inputOutputSection}>
            <InputOutputComponentButton
              componentType={AntibodyComponentType.AA_DOMAIN}
              componentName="AA domain"
              isSelected={outputTypes.has(NestedDataType.AA_DOMAIN)}
            />
            <InputOutputComponentButton
              componentType={AntibodyComponentType.AA_CHAIN}
              componentName="AA chain"
              isSelected={outputTypes.has(NestedDataType.AA_CHAIN)}
            />
          </div>
        </Grid.Col>
      </Grid>
    </div>
  );
}
